<template>
  <div class="garment-silhouette" :style="containerStyle" data-testid="garment-silhouette">
    <!-- Photo override (Q3): when a real per-color image exists it wins for
         THAT color -- render the photo INSTEAD of the tinted placeholder. -->
    <img
      v-if="photoUrl"
      :src="photoUrl"
      :alt="altText"
      class="garment-silhouette__photo"
      :style="photoStyle"
      :data-testid="`${testidPrefix}-photo`"
    />
    <template v-else>
      <!-- Solid garment shape filled with the swatch color, CSS-masked by the
           silhouette SVG so only the garment area is tinted. -->
      <div
        class="garment-silhouette__mask"
        :style="maskStyle"
        :data-testid="`${testidPrefix}-mask`"
      />
      <!-- The source SVG art multiply-blended on top for seam / knit detail.
           Opacity is keyed to whether the swatch is light or dark so the
           detail stays visible on both. -->
      <img
        :src="artUrl"
        :alt="altText"
        class="garment-silhouette__art"
        :style="artStyle"
        data-testid="garment-silhouette-art"
      />
    </template>
  </div>
</template>

<script>
// Tinted-silhouette garment placeholder (ADR 0060 / 0061, plan P3).
//
// Ports the design-handoff mechanism (design/shell.jsx `Silhouette`) to Vue 3:
// a solid <div> filled with the swatch color and CSS-masked by the silhouette
// SVG, PLUS the source SVG rendered as an <img> on top with mix-blend-mode
// multiply and opacity keyed to isLight(hex). A per-color photo, when present,
// replaces the tint entirely for that color.
//
// The 10 garment classes (tee, polo, crewneck, hoodie, fullZip, bottoms, bag,
// baseballCap, bucketHat, generic) are bundled from src/assets/silhouettes/.
// Caps have a "side" second view; every other garment -- including bottoms and
// bag -- has a "back" second view.
//
// Keep this map's key set aligned with the classes silhouetteFor.js and its
// backend mirror api/src/services/silhouette.py can emit. A class those can
// return but this map lacks does not fail loudly: it falls through
// resolvedSilhouette's hasOwnProperty guard and renders the generic torso,
// so a sweatpant is drawn as a shirt (#2874).

// Import the bundled SVGs as URLs. The repo's webpack SVG rule resolves a
// bare (no `?inline`) .svg import through `asset/resource` to an emitted-asset
// URL; the test runner (Vite) resolves it to a data: URI. Both are valid
// `mask-image: url(...)` / `<img src>` values.
import teeFront from '@/assets/silhouettes/tee-front.svg'
import teeBack from '@/assets/silhouettes/tee-back.svg'
import poloFront from '@/assets/silhouettes/polo-front.svg'
import poloBack from '@/assets/silhouettes/polo-back.svg'
import crewneckFront from '@/assets/silhouettes/crewneck-front.svg'
import crewneckBack from '@/assets/silhouettes/crewneck-back.svg'
import hoodieFront from '@/assets/silhouettes/hoodie-front.svg'
import hoodieBack from '@/assets/silhouettes/hoodie-back.svg'
import fullZipFront from '@/assets/silhouettes/fullZip-front.svg'
import fullZipBack from '@/assets/silhouettes/fullZip-back.svg'
import bottomsFront from '@/assets/silhouettes/bottoms-front.svg'
import bottomsBack from '@/assets/silhouettes/bottoms-back.svg'
import bagFront from '@/assets/silhouettes/bag-front.svg'
import bagBack from '@/assets/silhouettes/bag-back.svg'
import baseballCapFront from '@/assets/silhouettes/baseballCap-front.svg'
import baseballCapSide from '@/assets/silhouettes/baseballCap-side.svg'
import bucketHatFront from '@/assets/silhouettes/bucketHat-front.svg'
import bucketHatSide from '@/assets/silhouettes/bucketHat-side.svg'
import genericFront from '@/assets/silhouettes/generic-front.svg'
import genericBack from '@/assets/silhouettes/generic-back.svg'

// silhouette -> { view -> url }. Caps carry front/side; garments front/back.
const SILHOUETTES = {
  tee: { front: teeFront, back: teeBack },
  polo: { front: poloFront, back: poloBack },
  crewneck: { front: crewneckFront, back: crewneckBack },
  hoodie: { front: hoodieFront, back: hoodieBack },
  fullZip: { front: fullZipFront, back: fullZipBack },
  bottoms: { front: bottomsFront, back: bottomsBack },
  bag: { front: bagFront, back: bagBack },
  baseballCap: { front: baseballCapFront, side: baseballCapSide },
  bucketHat: { front: bucketHatFront, side: bucketHatSide },
  generic: { front: genericFront, back: genericBack },
}

const CAPS = new Set(['baseballCap', 'bucketHat'])

/**
 * The bundled art URL for a garment class's FRONT view (unknown class ->
 * generic). For callers that draw the silhouette themselves -- the open-canvas
 * export (card #243) -- so they use the same art this component shows.
 */
export function silhouetteArtUrl (silhouette) {
  const key = Object.prototype.hasOwnProperty.call(SILHOUETTES, silhouette)
    ? silhouette
    : 'generic'
  return SILHOUETTES[key].front
}

/**
 * Perceived-lightness test (ported from shell.jsx). Expands 3-digit hex,
 * parses r/g/b, and applies the ITU-R weighted sum against the same
 * threshold the handoff used. Light -> high-opacity blend; dark -> low.
 */
export function isLight (hex) {
  const raw = String(hex || '').replace('#', '')
  const full = raw.length === 3 ? raw.replace(/./g, (ch) => ch + ch) : raw
  const n = parseInt(full, 16)
  if (!Number.isFinite(n)) {
    // Unparseable hex -> treat as light so the seam detail stays visible.
    return true
  }
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return r * 299 + g * 587 + b * 114 > 150000
}

/**
 * Is this pad a zero length -- `0`, `0px`, `0%` and friends?
 *
 * Only a zero needs the special case: any non-zero value the callers pass
 * carries a unit or a percent, which calc() accepts. An unparseable value is
 * treated as non-zero so it keeps flowing into the calc() it always did.
 */
export function isZeroLength (value) {
  return /^-?0(?:\.0+)?(?:[a-z]+|%)?$/i.test(String(value == null ? '' : value).trim())
}

export default {
  name: 'GarmentSilhouette',

  props: {
    // Garment class: tee|polo|crewneck|hoodie|fullZip|bottoms|bag|
    // baseballCap|bucketHat|generic.
    // An unknown value falls back to `generic`.
    silhouette: {
      type: String,
      default: 'generic',
    },
    // Swatch color: { name, hex }. hex drives the tint + light/dark blend.
    color: {
      type: Object,
      default: () => ({ name: '', hex: '#c9d3db' }),
    },
    // View: front|back|side. Non-front resolves to `side` for caps, `back`
    // otherwise. An unknown view falls back to `front`.
    view: {
      type: String,
      default: 'front',
    },
    // Real per-color image URL. When present it REPLACES the tinted
    // silhouette for that color (photo wins).
    photoUrl: {
      type: String,
      default: null,
    },
    // Optional testid PREFIX for the photo / mask elements.
    //
    // A page can hold several silhouettes at once -- the SKU setup page
    // draws one per geometry stage AND one per filmstrip cell -- and the
    // default ids are the same in every instance, so a scenario selector
    // could not say WHICH garment it meant. ui-runner selectors are a bare
    // `[data-testid=...]` with no descendant combinator (R-1), so scoping
    // by an ancestor is not available; the id itself has to carry it.
    //
    // Defaults to the original value, so every existing caller and
    // scenario keeps the ids it already had.
    testidPrefix: {
      type: String,
      default: 'garment-silhouette',
    },
    // Inner padding so the garment never touches the frame edge.
    pad: {
      type: String,
      default: '16%',
    },
    // Fallback surface behind the garment (light neutral by default).
    bg: {
      type: String,
      default: '#f5f8fa',
    },
  },

  computed: {
    // Resolve the garment class, falling back to `generic`.
    resolvedSilhouette () {
      return Object.prototype.hasOwnProperty.call(SILHOUETTES, this.silhouette)
        ? this.silhouette
        : 'generic'
    },

    // Resolve the view for the chosen garment. `front` is universal; the
    // second view is `side` for caps and `back` for every other garment. Any
    // request for a non-front view (`back` or `side`, regardless of which the
    // garment actually has -- e.g. `back` asked of a cap) resolves to the
    // garment's actual second view; any unknown value collapses to `front`.
    resolvedView () {
      if (this.view === 'back' || this.view === 'side') {
        return CAPS.has(this.resolvedSilhouette) ? 'side' : 'back'
      }
      return 'front'
    },

    artUrl () {
      return SILHOUETTES[this.resolvedSilhouette][this.resolvedView]
    },

    colorHex () {
      return (this.color && this.color.hex) || '#c9d3db'
    },

    altText () {
      return (this.color && this.color.name)
        ? `${this.resolvedSilhouette} in ${this.color.name}`
        : this.resolvedSilhouette
    },

    containerStyle () {
      return { background: this.bg }
    },

    // Garment art is inset by `pad` on all sides.
    //
    // A ZERO PAD MUST NOT PRODUCE A calc(). Interpolating `pad="0"` into the
    // template below yields `calc(100% - 0 * 2)`, which is INVALID CSS -- inside
    // calc() a bare `0` is a <number>, and the grammar forbids subtracting a
    // number from a percentage. A browser discards the whole declaration, so the
    // element falls back to `width: auto` and an <img> paints at its INTRINSIC
    // size: the geometry stage passes `pad="0"` and Jake's 1200x1800 photo
    // rendered at 1200x1800 inside a 558x380 frame (verified in headless Chrome).
    //
    // It failed silently in tests because jsdom's CSS engine does not validate
    // calc() -- it stores the malformed string verbatim, so an assertion looking
    // for "100%" inside it found what it wanted and passed against the bug.
    //
    // Returning a bare `100%` for a zero pad sidesteps the grammar entirely
    // rather than papering over it with `0px`, and it keeps the common case the
    // simplest declaration available.
    innerSizeStyle () {
      const extent = isZeroLength(this.pad)
        ? '100%'
        : `calc(100% - ${this.pad} * 2)`
      return {
        width: extent,
        height: extent,
      }
    },

    maskStyle () {
      const url = `url(${this.artUrl})`
      return {
        ...this.innerSizeStyle,
        background: this.colorHex,
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }
    },

    artStyle () {
      return {
        ...this.innerSizeStyle,
        objectFit: 'contain',
        mixBlendMode: 'multiply',
        opacity: isLight(this.colorHex) ? 0.9 : 0.42,
      }
    },

    photoStyle () {
      return {
        ...this.innerSizeStyle,
        objectFit: 'contain',
      }
    },
  },
}
</script>

<style scoped>
.garment-silhouette {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.garment-silhouette__mask,
.garment-silhouette__art,
.garment-silhouette__photo {
  position: absolute;
}
</style>
